// HiveShare thumbnail utilities

import type { AnimatedThumbnailOptions, ThumbnailUrls } from '../types/hiveshare';
import { buildThumbnailUrls, getResponsiveThumbnailOptions, isThumbnailSupported } from '../config/hiveshare';

/**
 * Generate thumbnail URLs for a Cloudflare Stream video
 */
export const generateThumbnailUrls = (
  videoId: string,
  options: Partial<AnimatedThumbnailOptions> = {},
  breakpoint: 'mobile' | 'tablet' | 'desktop' = 'desktop'
): ThumbnailUrls => {
  if (!videoId || !isThumbnailSupported()) {
    return {
      static: '',
      animated: ''
    };
  }

  // Get responsive sizing
  const responsiveOptions = getResponsiveThumbnailOptions(breakpoint);
  const mergedOptions = { ...responsiveOptions, ...options };

  return {
    static: buildThumbnailUrls.static(videoId, mergedOptions),
    animated: buildThumbnailUrls.animated(videoId, mergedOptions)
  };
};

/**
 * Generate just the animated thumbnail URL
 */
export const getAnimatedThumbnailUrl = (
  videoId: string,
  options: Partial<AnimatedThumbnailOptions> = {},
  breakpoint: 'mobile' | 'tablet' | 'desktop' = 'desktop'
): string => {
  if (!videoId || !isThumbnailSupported()) {
    return '';
  }

  const responsiveOptions = getResponsiveThumbnailOptions(breakpoint);
  const mergedOptions = { ...responsiveOptions, ...options };
  
  return buildThumbnailUrls.animated(videoId, mergedOptions);
};

/**
 * Generate just the static thumbnail URL
 */
export const getStaticThumbnailUrl = (
  videoId: string,
  options: Partial<Pick<AnimatedThumbnailOptions, 'time' | 'height' | 'width' | 'fit'>> = {},
  breakpoint: 'mobile' | 'tablet' | 'desktop' = 'desktop'
): string => {
  if (!videoId || !isThumbnailSupported()) {
    return '';
  }

  const responsiveOptions = getResponsiveThumbnailOptions(breakpoint);
  const mergedOptions = { ...responsiveOptions, ...options };
  
  return buildThumbnailUrls.static(videoId, mergedOptions);
};

/**
 * Preload thumbnail images for better performance
 */
export const preloadThumbnails = (urls: ThumbnailUrls): Promise<void[]> => {
  const preloadPromises = [urls.static, urls.animated]
    .filter(Boolean)
    .map(url => {
      return new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => reject(new Error(`Failed to preload ${url}`));
        img.src = url;
      });
    });

  return Promise.all(preloadPromises);
};

/**
 * Check if an image URL is valid and loads successfully
 */
export const validateThumbnailUrl = (url: string): Promise<boolean> => {
  if (!url) return Promise.resolve(false);
  
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
};

/**
 * Smart thumbnail time selection based on video duration
 * Returns a good starting point for thumbnail generation
 */
export const getOptimalThumbnailTime = (durationSeconds?: number): string => {
  if (!durationSeconds || durationSeconds <= 0) {
    return '5s'; // Default fallback
  }

  // For very short videos (< 30s), start earlier
  if (durationSeconds < 30) {
    return '2s';
  }
  
  // For short videos (30s - 2min), start at 5s
  if (durationSeconds < 120) {
    return '5s';
  }
  
  // For medium videos (2-10min), start at 10s
  if (durationSeconds < 600) {
    return '10s';
  }
  
  // For longer videos, start at 15s
  return '15s';
};

/**
 * Generate optimized thumbnail options based on video properties
 */
export const getOptimizedThumbnailOptions = (
  durationSeconds?: number,
  aspectRatio?: number,
  breakpoint: 'mobile' | 'tablet' | 'desktop' = 'desktop'
): Partial<AnimatedThumbnailOptions> => {
  const responsiveOptions = getResponsiveThumbnailOptions(breakpoint);
  
  return {
    ...responsiveOptions,
    time: getOptimalThumbnailTime(durationSeconds),
    duration: durationSeconds && durationSeconds < 10 ? '2s' : '4s', // Shorter animation for short videos
    fps: breakpoint === 'mobile' ? 8 : 10 // Lower FPS on mobile for performance
  };
};

/**
 * Detect user's device/connection preference for thumbnail quality
 */
export const getDeviceOptimizedOptions = (): Partial<AnimatedThumbnailOptions> => {
  // Check for reduced motion preference
  const prefersReducedMotion = typeof window !== 'undefined' && 
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  // Check for slow connection
  const connection = typeof navigator !== 'undefined' && 
    'connection' in navigator ? 
    (navigator as any).connection : null;
  
  const isSlowConnection = connection && (
    connection.effectiveType === 'slow-2g' || 
    connection.effectiveType === '2g'
  );

  if (prefersReducedMotion || isSlowConnection) {
    return {
      fps: 6,        // Lower FPS
      duration: '2s' // Shorter duration
    };
  }

  return {};
};

// Default export with commonly used functions
export default {
  generateThumbnailUrls,
  getAnimatedThumbnailUrl,
  getStaticThumbnailUrl,
  preloadThumbnails,
  validateThumbnailUrl,
  getOptimalThumbnailTime,
  getOptimizedThumbnailOptions,
  getDeviceOptimizedOptions
};